import type {
  Asset,
  AssetPayload,
  AssetLink,
  OperationMode,
  OperationModePayload,
  AssetTransitionDTO,
  AssetLinkDTO,
  AssetTransition,
  AssetEditableFields,
  ProductionOperationMode,
  KpiVisibilityFlag,
  UpmEntityType,
  EntityConfigDto,
  Kpi
} from '@hconnect/common/types'

import type {
  AssetStandardOperationTimePayload,
  AssetStandardOperationTime,
  DefaultAssetOperationTimePayload,
  DefaultAssetOperationTime,
  AssetStandardOperationTimeConflictResolutionAction
} from '../modules/assets/types'
import {KpiPayload} from '../modules/plant-setup/plant-setup-tabs/kpis/types'

import {Api} from './api'

import {measurementsMutations} from '@settings/modules/measurements/api/mutations'
import {
  AddGrindingCircuitPayload,
  GrindingCircuit,
  UpdateGrindingCircuitPayload
} from '@settings/modules/plant-setup/types'
import {
  AreaCreateType,
  DepartmentCreateType,
  ProductionLine,
  ProductionLineCreateType
} from '@settings/modules/plant-setup/types/departmentConfig'
import {samplingPointsMutations} from '@settings/modules/sampling-points/api/mutations'
import {Equipment} from '@settings/modules/upm/types/equipment'

type UpdateValidateStandardOperationTimeParams = {
  plantCode: string
  defaultOperationTimeId: number
  AssetStandardOperationTimeDto: AssetStandardOperationTimePayload
}
type UpdateStandardOperationTimeParams = UpdateValidateStandardOperationTimeParams & {
  onConflict: AssetStandardOperationTimeConflictResolutionAction
}

type AddValidateStandardOperationTimeParams = {
  plantCode: string
  AssetStandardOperationTimeDto: AssetStandardOperationTimePayload
}
type AddStandardOperationTimeParams = AddValidateStandardOperationTimeParams & {
  onConflict: AssetStandardOperationTimeConflictResolutionAction
}

export const mutations = {
  addDefaultAssetOperationTime: async ({
    assetOperationModeId,
    plantCode,
    defaultAssetOperationTimeDto
  }: {
    plantCode: string
    assetOperationModeId: number
    defaultAssetOperationTimeDto: DefaultAssetOperationTimePayload
  }): Promise<DefaultAssetOperationTime> => {
    const path = `/schedules/default-asset-operation-times/${plantCode}/${assetOperationModeId}`
    const response = await Api.axiosInstance.post<DefaultAssetOperationTime>(
      path,
      defaultAssetOperationTimeDto
    )
    return response.data
  },
  updateDefaultAssetOperationTime: async ({
    plantCode,
    assetOperationModeId,
    defaultOperationTimeId,
    defaultAssetOperationTimeDto
  }: {
    plantCode: string
    assetOperationModeId: number
    defaultOperationTimeId: number
    defaultAssetOperationTimeDto: DefaultAssetOperationTimePayload
  }): Promise<void> => {
    const path = `/schedules/default-asset-operation-times/${plantCode}/${assetOperationModeId}/${defaultOperationTimeId}`
    const response = await Api.axiosInstance.put<void>(path, defaultAssetOperationTimeDto)
    return response.data
  },
  deleteDefaultAssetOperationTime: async ({
    plantCode,
    assetOperationModeId,
    defaultAssetOperationTimeId
  }: {
    plantCode: string
    assetOperationModeId: number
    defaultAssetOperationTimeId: number
  }): Promise<void> => {
    const path = `/schedules/default-asset-operation-times/${plantCode}/${assetOperationModeId}/${defaultAssetOperationTimeId}`
    const response = await Api.axiosInstance.delete<void>(path)
    return response.data
  },
  addStandardOperationTime: async ({
    plantCode,
    onConflict,
    AssetStandardOperationTimeDto
  }: AddStandardOperationTimeParams): Promise<AssetStandardOperationTime> => {
    const path = `/plants/${plantCode}/schedules/standard-operation-times`
    const params = {
      onConflict
    }
    const response = await Api.axiosInstance.post<AssetStandardOperationTime>(
      path,
      AssetStandardOperationTimeDto,
      {params}
    )
    return response.data
  },
  updateStandardOperationTime: async ({
    plantCode,
    onConflict,
    defaultOperationTimeId,
    AssetStandardOperationTimeDto
  }: UpdateStandardOperationTimeParams): Promise<void> => {
    const path = `/plants/${plantCode}/schedules/standard-operation-times/${defaultOperationTimeId}`
    const params = {
      onConflict
    }
    const response = await Api.axiosInstance.put<void>(path, AssetStandardOperationTimeDto, {
      params
    })
    return response.data
  },
  deleteStandardOperationTime: async ({
    plantCode,
    AssetStandardOperationTimeId
  }: {
    plantCode: string
    AssetStandardOperationTimeId: number
  }): Promise<void> => {
    const path = `/plants/${plantCode}/schedules/standard-operation-times/${AssetStandardOperationTimeId}`
    const response = await Api.axiosInstance.delete<void>(path)
    return response.data
  },
  addGrindingCircuit: async ({
    plantCode,
    grindingCircuitDto
  }: AddGrindingCircuitPayload): Promise<GrindingCircuit> => {
    const path = `/plants/${plantCode}/grinding-circuits`
    const response = await Api.axiosInstance.post<GrindingCircuit>(path, grindingCircuitDto)
    return response.data
  },
  updateGrindingCircuit: async ({
    plantCode,
    grindingCircuitDto
  }: UpdateGrindingCircuitPayload): Promise<GrindingCircuit> => {
    const path = `/plants/${plantCode}/grinding-circuits/${grindingCircuitDto.id}`
    const response = await Api.axiosInstance.put<GrindingCircuit>(path, grindingCircuitDto)
    return response.data
  },
  deleteGrindingCircuit: async ({
    plantCode,
    grindingCircuitId
  }: {
    plantCode: string
    grindingCircuitId: number
  }): Promise<void> => {
    const path = `/plants/${plantCode}/grinding-circuits/${grindingCircuitId}`
    const response = await Api.axiosInstance.delete<void>(path)
    return response.data
  },
  addAsset: async ({
    plantCode,
    name,
    type,
    upmId
  }: {plantCode: string} & AssetPayload): Promise<Asset> => {
    const path = `/plants/${plantCode}/assets`
    const response = await Api.axiosInstance.post<Asset>(path, {
      name,
      type,
      ...(upmId ? {upmId} : {})
    })
    return response.data
  },
  editAsset: async ({plantCode, assetId, key, dto}) => {
    const keyToPath: Record<AssetEditableFields, string> = {
      isOptimized: 'optimized',
      startupCost: 'startup-cost',
      minimumDowntime: 'minimum-downtime',
      isShutdownAvailable: 'is-shutdown-available',
      name: 'name',
      type: 'type',
      startCoefficient: 'start-coefficient',
      stopCoefficient: 'stop-coefficient',
      upmId: 'upm-id'
    }
    const path = `/plants/${plantCode}/assets/${assetId}/${keyToPath[key]}`
    const response = await Api.axiosInstance.patch<Asset>(path, dto)
    return response.data
  },
  deleteAsset: async ({
    plantCode,
    assetId
  }: {
    plantCode: string
    assetId: number
  }): Promise<void> => {
    const path = `/plants/${plantCode}/assets/${assetId}`
    await Api.axiosInstance.delete<void>(path)
  },
  addOperationMode: async ({
    plantCode,
    assetId,
    ...dto
  }: {plantCode: string; assetId: number} & Omit<
    OperationModePayload,
    'isOptimized'
  >): Promise<OperationMode> => {
    const path = `/plants/${plantCode}/assets/${assetId}/operation-modes`
    const response = await Api.axiosInstance.post<OperationMode>(path, dto)
    return response.data
  },
  editOperationMode: async ({
    plantCode,
    assetId,
    operationModeId,
    key,
    dto
  }: {
    plantCode: string
    assetId: number
    operationModeId: number
    key: keyof OperationModePayload
    dto: OperationModePayload
  }): Promise<ProductionOperationMode> => {
    const keyToResourceMap: Record<
      keyof OperationModePayload,
      | 'recipe'
      | 'optimized'
      | 'name'
      | 'type'
      | 'throughput'
      | 'power-consumption'
      | 'minimum-runtime'
    > = {
      recipeId: 'recipe',
      isOptimized: 'optimized',
      name: 'name',
      type: 'type',
      throughput: 'throughput',
      powerConsumption: 'power-consumption',
      minimumRuntime: 'minimum-runtime'
    }
    const resource = keyToResourceMap[key]

    const path = `/plants/${plantCode}/assets/${assetId}/operation-modes/${operationModeId}/${resource}`
    const response = await Api.axiosInstance.patch<ProductionOperationMode>(path, dto)
    return response.data
  },
  deleteOperationMode: async ({
    plantCode,
    assetId,
    operationModeId
  }: {
    plantCode: string
    assetId: number
    operationModeId: number
  }): Promise<void> => {
    const path = `/plants/${plantCode}/assets/${assetId}/operation-modes/${operationModeId}`
    await Api.axiosInstance.delete<void>(path)
  },
  editAssetTransition: async ({
    plantCode,
    assetId,
    id,
    key,
    dto
  }: {
    plantCode: string
    assetId: number
    id: number
    key: keyof AssetTransitionDTO
    dto: AssetTransitionDTO
  }): Promise<AssetTransition> => {
    const keyToResourceMap: Record<keyof AssetTransitionDTO, 'time' | 'is-possible'> = {
      time: 'time',
      isPossible: 'is-possible'
    }
    const resource = keyToResourceMap[key]
    const path = `/plants/${plantCode}/assets/${assetId}/transitions/${id}/${resource}`
    const response = await Api.axiosInstance.patch<AssetTransition>(path, dto)
    return response.data
  },
  addAssetLink: async ({
    plantCode,
    dto
  }: {
    plantCode: string
    dto: AssetLinkDTO
  }): Promise<AssetLink> => {
    const path = `/plants/${plantCode}/assets/links`
    const response = await Api.axiosInstance.post<AssetLink>(path, dto)
    return response.data
  },
  editAssetLink: async ({
    plantCode,
    id,
    dto
  }: {
    plantCode: string
    id: number
    dto: AssetLinkDTO
  }): Promise<AssetLink> => {
    const path = `/plants/${plantCode}/assets/links/${id}`
    const response = await Api.axiosInstance.patch<AssetLink>(path, dto)
    return response.data
  },
  deleteAssetLink: async ({plantCode, id}: {plantCode: string; id: number}): Promise<void> => {
    const path = `/plants/${plantCode}/assets/links/${id}`
    await Api.axiosInstance.delete<void>(path)
  },
  requestSalesForecast: async ({
    plantCode,
    materialId,
    productId
  }: {
    plantCode: string
    materialId: number
    productId: number
  }): Promise<void> => {
    const path = `/plants/${plantCode}/material-demand/${materialId}/demand/prediction/${productId}`
    await Api.axiosInstance.post<void>(path)
  },
  createKpi: async ({plantCode, kpiData}: {plantCode: string; kpiData: KpiPayload}) => {
    const path = `/kpieditor/plants/${plantCode}/kpis`
    const response = await Api.axiosInstance.post<Kpi>(path, kpiData)
    return response.data
  },
  deleteKpi: async ({plantCode, kpiDefinitionId}) => {
    const path = `/kpieditor/plants/${plantCode}/kpis/${kpiDefinitionId}`
    await Api.axiosInstance.delete<void>(path)
  },
  editKpiDisplayName: async ({plantCode, kpiId, displayName}) => {
    const path = `/kpieditor/plants/${plantCode}/kpis/${kpiId}/displayname`
    const response = await Api.axiosInstance.patch<Kpi>(path, {displayName})
    return response.data
  },
  editKpiFrequency: async ({plantCode, kpiId, frequency}) => {
    const path = `/kpieditor/plants/${plantCode}/kpis/${kpiId}/frequency`
    const response = await Api.axiosInstance.patch<Kpi>(path, {frequency})
    return response.data
  },
  editKpiUnit: async ({plantCode, kpiId, unit}) => {
    const path = `/kpieditor/plants/${plantCode}/kpis/${kpiId}/unit`
    const response = await Api.axiosInstance.patch<Kpi>(path, {unit})
    return response.data
  },
  editKpiAggregationType: async ({plantCode, kpiId, aggregationTypeId}) => {
    const path = `/kpieditor/plants/${plantCode}/kpis/${kpiId}/aggregation-type`
    const response = await Api.axiosInstance.patch<Kpi>(path, aggregationTypeId, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    return response.data
  },
  editKpiWeight: async ({plantCode, kpiId, weightKpiId}) => {
    const path = `/kpieditor/plants/${plantCode}/kpis/${kpiId}/weight-kpi`
    const response = await Api.axiosInstance.patch<Kpi>(path, weightKpiId, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    return response.data
  },
  editKpiThresholds: async ({plantCode, kpiId, thresholds}) => {
    const path = `/kpieditor/plants/${plantCode}/kpis/${kpiId}/ranges`
    const response = await Api.axiosInstance.patch<Kpi>(path, {...thresholds})
    return response.data
  },
  verifyKpiFormula: async ({plantCode, kpiId, formula}) => {
    const path = `/kpieditor/plants/${plantCode}/kpis/${kpiId}/formula`
    const response = await Api.axiosInstance.patch<Kpi>(path, {formula})
    return response.data
  },
  editKpiVisibilityFlags: async ({
    plantCode,
    kpiId,
    kpiVisibilityFlags
  }: {
    plantCode: string
    kpiId: number
    kpiVisibilityFlags: KpiVisibilityFlag[]
  }) => {
    const path = `/kpieditor/plants/${plantCode}/kpis/${kpiId}/visibility`
    const response = await Api.axiosInstance.patch<Kpi>(path, kpiVisibilityFlags)
    return response.data
  },
  editJanusKeyFigure: async ({
    plantUpmId,
    specificKpiId,
    kpiDefinitionId
  }: {
    plantUpmId: string
    specificKpiId: string
    kpiDefinitionId: number | null
  }) => {
    const path = `/janus-kpi/janus-specific-kpi/${plantUpmId}/${specificKpiId}`
    const response = await Api.axiosInstance.patch<Kpi>(path, {
      kpiDefinitionId: String(kpiDefinitionId)
    })
    return response.data
  },
  addEntityConfig: async ({
    plantUpmId,
    entityConfig,
    type
  }: {
    plantUpmId: string
    entityConfig: EntityConfigDto
    type: UpmEntityType
  }) => {
    const path = `asset-config/janus-production-data/plant/${plantUpmId}/upm-type/${type}?typeUpmId=${entityConfig.metadata.typeUpmId}`
    return (await Api.axiosInstance.post(path, {...entityConfig.payload})).data
  },
  updateEntityConfig: async ({
    plantUpmId,
    entityConfig,
    type
  }: {
    plantUpmId: string
    entityConfig: EntityConfigDto
    type: UpmEntityType
  }) => {
    const path = `asset-config/janus-production-data/plant/${plantUpmId}/upm-type/${type}?typeUpmId=${entityConfig.metadata.typeUpmId}`
    return (await Api.axiosInstance.put(path, {...entityConfig.payload})).data
  },
  createArea: async (area: AreaCreateType): Promise<void> => {
    const path = 'upm/area'
    await Api.axiosInstance.post<void>(path, area)
  },
  updateDepartment: async (department: Equipment): Promise<void> => {
    const path = `upm/department/${department.id}`
    await Api.axiosInstance.put<void>(path, department)
  },
  deleteDepartment: async (id: string): Promise<void> => {
    const path = `upm/department/${id}`
    await Api.axiosInstance.delete<void>(path)
  },
  createDepartment: async (department: DepartmentCreateType): Promise<void> => {
    const path = 'upm/department'
    await Api.axiosInstance.post<void>(path, department)
  },
  deleteEquipment: async (id: string): Promise<void> => {
    const path = `upm/equipment/${id}`
    await Api.axiosInstance.delete<void>(path)
  },
  createProductionLine: async (
    productionLine: ProductionLineCreateType
  ): Promise<ProductionLine> => {
    const path = 'upm/production-line'
    const response = await Api.axiosInstance.post<ProductionLine>(path, productionLine)
    return response.data
  },
  deleteGlobalMaterial: async ({
    plantCode,
    materialId
  }: {
    plantCode: string
    materialId: number
  }): Promise<void> => {
    const path = `/plants/${plantCode}/materials/${materialId}/global-material`
    await Api.axiosInstance.delete<void>(path)
  },
  createJanusKpis: async ({plantCode}: {plantCode: string}): Promise<void> => {
    const path = `/janus-kpi/create/${plantCode}`
    await Api.axiosInstance.post(path)
  },
  restoreDepartment: async (id: string): Promise<void> => {
    const path = `upm/department/${id}/undelete`
    await Api.axiosInstance.patch<void>(path)
  },
  restoreEquipment: async (id: string): Promise<void> => {
    const path = `upm/equipment/${id}/undelete`
    await Api.axiosInstance.patch<void>(path)
  },
  ...measurementsMutations,
  ...samplingPointsMutations
}

export type Mutations = typeof mutations
